import { env } from '@/config/env';

/**
 * B6 · Lightweight observability.
 *
 * Captures uncaught errors + unhandled rejections. Env-gated: if
 * VITE_SENTRY_DSN (or any collector URL) is set we beacon a minimal payload
 * there; otherwise we log to console. Designed so `@sentry/react` can be
 * swapped in as a drop-in upgrade without changing call sites.
 */

export interface SerializedError {
  message: string;
  stack?: string;
  kind: string;
  at: string;
}

/** Pure, defensive error serialiser — never throws on any input. */
export const serializeError = (err: unknown): SerializedError => {
  const at = new Date().toISOString();
  if (err instanceof Error) {
    return { message: err.message || 'Error', stack: err.stack, kind: err.name || 'Error', at };
  }
  if (typeof err === 'string') return { message: err, kind: 'string', at };
  try {
    return { message: JSON.stringify(err) ?? String(err), kind: typeof err, at };
  } catch {
    return { message: 'Unserialisable error', kind: typeof err, at };
  }
};

let installed = false;

/** Wire global error capture. Idempotent + SSR-safe. */
export const initObservability = (): void => {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  const report = (err: unknown, context: string) => {
    const payload = { ...serializeError(err), context, surface: env.surface };
    // eslint-disable-next-line no-console
    console.error('[observability]', payload);
    if (env.sentryDsn) {
      try {
        navigator.sendBeacon?.(env.sentryDsn, JSON.stringify(payload));
      } catch {
        // reporting must never throw
      }
    }
  };

  window.addEventListener('error', (e) => report(e.error ?? e.message, 'window.error'));
  window.addEventListener('unhandledrejection', (e) => report(e.reason, 'unhandledrejection'));
};
